'use client';

import type { FC, RefObject } from 'react';
import { useMemo } from 'react';
import { Button, OverlayBadge } from '../../components/ui';
import { InfiniteItemGrid } from '../../components/InfiniteItemGrid';
import { PagedItemGrid } from '../../components/PagedItemGrid';
import { useInfinitePager } from '../../hooks/useInfinitePager';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useNavigation } from '../../contexts/NavigationContext';
import type { UserInventoryItemResponse } from '../../services/api';
import { ItemListItem } from '../../components/ItemListItem';
import { ItemPrice, useSteamPriceAllowed } from '../../components/ItemPrice';
import { MIN_UPGRADE_ITEM_PRICE, useUpgradeInventoryInfinite } from '../../queries/useUpgradeQueries';
import { formatCreditsNumber } from '../../utils/formatters';
import translations from './UpgradeScreen.translations';
import { cn } from '../../utils/cn';
import { UPGRADE_COMPACT_CARD_CLASS, UPGRADE_COMPACT_MIN_COLUMN, UPGRADE_SELECTED_CARD_CLASS } from './upgradeCards';
import type { UpgradeItem } from './UpgradeScreen';

interface UpgradeInventoryListProps {
  onSelectItem: (item: UpgradeItem) => void;
  scrollRef: RefObject<HTMLDivElement | null>;
  paged?: boolean;
  selectedId?: string | null;
}

export const UpgradeInventoryList: FC<UpgradeInventoryListProps> = ({ onSelectItem, scrollRef, paged = false, selectedId = null }) => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const steamPriceAllowed = useSteamPriceAllowed();
  const query = useUpgradeInventoryInfinite();
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    refetch,
  } = query;
  const pager = useInfinitePager(query, 'inventory');

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  const renderItem = (item: UserInventoryItemResponse, index: number) => {
    const price = item.acquiredPriceInCredits;

    return (
      <ItemListItem
        key={`${item.item.hashName}-${index}`}
        item={{
          ...item.item,
          imageUrl: item.item.imageUrl || '',
          source: item.source,
        }}
        className={cn(paged && UPGRADE_COMPACT_CARD_CLASS, item.id === selectedId && UPGRADE_SELECTED_CARD_CLASS)}
        onClick={() =>
          onSelectItem({
            id: item.id,
            hashName: item.item.hashName,
            name: item.item.name,
            imageUrl: item.item.imageUrl || '',
            rarityColor: item.item.rarityColor || '#4cc9f0',
            rarityName: item.item.rarityName || undefined,
            price: price || 0,
            statTrak: item.item.statTrak,
            souvenir: item.item.souvenir,
          })
        }
      >
        <OverlayBadge>
          <ItemPrice credits={price || 0} variant="cardPrice" />
        </OverlayBadge>
        {steamPriceAllowed && item.item.steamPrice != null && (
          <OverlayBadge>
            <ItemPrice steamPrice={item.item.steamPrice} steamIconSize={11} variant="steamPrice" />
          </OverlayBadge>
        )}
      </ItemListItem>
    );
  };

  const emptyAction = (
    <Button size="l" onClick={() => navigation.navigate('cases')} data-testid="upgrade-empty-go-cases">
      {t('goToCases')}
    </Button>
  );
  const emptyDescription = t('noSuitableItemsHint', { min: formatCreditsNumber(MIN_UPGRADE_ITEM_PRICE) });

  if (paged) {
    return (
      <PagedItemGrid
        items={pager.current?.items ?? []}
        page={pager.page}
        totalPages={null}
        hasPrev={pager.hasPrev}
        hasNext={pager.hasNext}
        onPrev={pager.prev}
        onNext={pager.next}
        isLoading={isLoading}
        isError={isError}
        refetch={() => refetch()}
        renderItem={renderItem}
        emptyTitle={t('noSuitableItems')}
        emptyDescription={emptyDescription}
        emptyAction={emptyAction}
        errorText={t('errorLoadInventory')}
        minColumn={UPGRADE_COMPACT_MIN_COLUMN}
        skeletonCardClassName={UPGRADE_COMPACT_CARD_CLASS}
      />
    );
  }

  return (
    <InfiniteItemGrid
      items={items}
      isLoading={isLoading}
      isError={isError}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={() => fetchNextPage()}
      refetch={() => refetch()}
      scrollRef={scrollRef}
      renderItem={renderItem}
      emptyTitle={t('noSuitableItems')}
      emptyDescription={emptyDescription}
      emptyAction={emptyAction}
      errorText={t('errorLoadInventory')}
    />
  );
};
